import { useCallback, useEffect, useMemo, useState } from "react";
import { Dialog } from "@/components/ui/Dialog";
import { Spinner } from "@/components/ui/Spinner";
import { markTimesheetAtContact, timesheetAtContactFields } from "@/lib/timesheets";
import { TimesheetDetailFooter } from "@/features/timesheetFlow/components/TimesheetDetailFooter";
import { TimesheetProjectFooterIcons } from "@/features/timesheetFlow/components/TimesheetProjectFooterIcons";
import { useTimesheetFlowLayers } from "@/features/timesheetFlow/timesheetFlowLayers";
import { TimesheetExportActions } from "@/features/timesheetFlow/components/TimesheetExportActions";
import {
  archiveTimesheet,
  getPublicLinks,
  getTimesheetDetail,
  logTimesheetPrint,
  runPrintByLocationWithFeedback,
  sendTimesheetMail,
  unarchiveTimesheet,
} from "@/features/timesheetFlow/timesheetFlowApi";
import { useTimesheetInvoiceDraftControls } from "@/features/timesheetFlow/useTimesheetInvoiceDraftControls";
import type { TimesheetPermissions } from "@/features/timesheetFlow/timesheetPermissions";
import type { TimesheetRecord } from "@/features/timesheetFlow/timesheetFlowTypes";
import {
  resolveTimesheetMailPdfUrl,
  resolveTimesheetRecordStatus,
} from "@/pages/stundennachweise/timesheetFilterLogic";

function fmtDateDe(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("de-DE");
}

function formatPeriod(ts: TimesheetRecord): string {
  const from = fmtDateDe(ts.date_from);
  const to = fmtDateDe(ts.date_to);
  if (from && to) return from === to ? from : `${from} – ${to}`;
  return from || to || "";
}

export function TimesheetActionsModal({
  timesheetId,
  permissions,
  open,
  onClose,
  onRefresh,
}: {
  timesheetId: number | null;
  permissions: TimesheetPermissions | null;
  open: boolean;
  onClose: () => void;
  onRefresh: () => void;
}) {
  const [ts, setTs] = useState<TimesheetRecord | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [publicLinks, setPublicLinks] = useState<{ draft_url?: string; signed_url?: string }>({});
  const layers = useTimesheetFlowLayers();

  const reload = useCallback(async () => {
    if (!timesheetId) return;
    setLoading(true);
    setError(null);
    try {
      const [bundle, links] = await Promise.all([
        getTimesheetDetail(timesheetId),
        getPublicLinks(timesheetId).catch(() => ({})),
      ]);
      setTs(bundle.timesheet);
      setPublicLinks(links);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Laden fehlgeschlagen.");
      setTs(null);
    } finally {
      setLoading(false);
    }
  }, [timesheetId]);

  useEffect(() => {
    if (!open || !timesheetId) {
      setTs(null);
      setError(null);
      return;
    }
    void reload();
  }, [open, timesheetId, reload]);

  const normalizedStatus = ts ? resolveTimesheetRecordStatus(ts) : "draft";
  const {
    footerInvoiceActions,
    handleInvoiceAction,
    invoiceBusy,
    choiceDialog,
  } = useTimesheetInvoiceDraftControls({
    timesheetId: timesheetId ?? 0,
    timesheet: ts,
    normalizedStatus,
    canBilling: Boolean(permissions?.canBilling),
    onReload: reload,
    onRefresh,
    onError: setError,
  });
  const isSigned =
    (normalizedStatus !== "draft" &&
      normalizedStatus !== "ready" &&
      normalizedStatus !== "archived") ||
    Boolean(ts?.company_signed_at);
  const hidePrintMail = normalizedStatus === "signed";
  const showExportActions = permissions?.canManage && !hidePrintMail;
  const displayNumber = ts?.display_number || (timesheetId ? String(timesheetId) : "");
  const pdfLink = isSigned ? publicLinks.signed_url : publicLinks.draft_url;

  const metaParts = useMemo(() => {
    if (!ts) return [];
    const bits: string[] = [];
    const projectLabel =
      (ts.auftrag_display as string | undefined) ||
      (ts.auftragsnummer as string | undefined) ||
      (ts.kunde as string | undefined);
    if (projectLabel) bits.push(projectLabel);
    const period = formatPeriod(ts);
    if (period) bits.push(`Zeitraum: ${period}`);
    return bits;
  }, [ts]);

  const handleAtContactChange = useCallback(
    (checked: boolean) => {
      if (!ts) return;
      const previous = Boolean(ts.at_contact);
      setTs((prev) => (prev ? { ...prev, ...timesheetAtContactFields(checked) } : prev));
      void markTimesheetAtContact(ts.id, checked).catch(() => {
        setTs((prev) => (prev ? { ...prev, ...timesheetAtContactFields(previous) } : prev));
        setError("NW bei AP konnte nicht gespeichert werden.");
      });
    },
    [ts],
  );

  const handlePrint = async (locationId: number, copies: number) => {
    if (!timesheetId || !ts) return;
    const links =
      publicLinks.draft_url || publicLinks.signed_url
        ? publicLinks
        : await getPublicLinks(timesheetId);
    const url = resolveTimesheetMailPdfUrl(ts, links);
    const job = await runPrintByLocationWithFeedback(locationId, copies, url);
    await logTimesheetPrint(timesheetId, {
      location_id: locationId,
      copies,
      printer_name: job.printer_name || job.printer || job.result?.printer || undefined,
    });
    await reload();
    onRefresh();
  };

  const handleSendMail = async (payload: {
    recipients: string[];
    cc?: string[];
    subject: string;
    message: string;
    pdfUrl: string;
    htmlBody?: string;
    reminder?: boolean;
    markContact?: boolean;
  }) => {
    if (!timesheetId) return;
    await sendTimesheetMail(timesheetId, {
      recipients: payload.recipients,
      cc: payload.cc,
      subject: payload.subject,
      message: payload.message,
      html_body: payload.htmlBody,
      pdf_url: payload.pdfUrl,
      reminder: payload.reminder,
      mark_contact: payload.markContact ?? !payload.reminder,
    });
    await reload();
    onRefresh();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={timesheetId ? `Stundennachweis ${displayNumber}` : "Stundennachweis"}
      size="lg"
      zIndexClass={layers.nested}
      fullScreenMobile
      panelClassName="flex max-h-[min(92vh,44rem)] flex-col"
      bodyClassName="min-h-0 flex-1 overflow-y-auto"
      footer={
        ts && permissions ? (
          <TimesheetDetailFooter
            onClose={onClose}
            nwAtContact={
              permissions.canManage && normalizedStatus === "ready"
                ? {
                    checked: Boolean(ts.at_contact),
                    onChange: handleAtContactChange,
                  }
                : undefined
            }
            iconActions={<TimesheetProjectFooterIcons timesheet={ts} />}
            actions={[
              {
                id: "open-pdf",
                label: "PDF anzeigen",
                variant: "primary",
                onClick: () => {
                  if (pdfLink) window.open(pdfLink, "_blank", "noopener");
                },
                hidden: !pdfLink,
              },
              {
                id: "archive",
                label: normalizedStatus === "archived" ? "Wiederherstellen" : "Archivieren",
                onClick: () => {
                  setBusy(true);
                  const fn = normalizedStatus === "archived" ? unarchiveTimesheet : archiveTimesheet;
                  void fn(timesheetId!)
                    .then(() => {
                      onRefresh();
                      onClose();
                    })
                    .finally(() => setBusy(false));
                },
                hidden: !permissions.canArchive,
                disabled: busy,
              },
              ...footerInvoiceActions.map((action) => ({
                id: `invoice-${action.mode}${action.provider ? `-${action.provider}` : ""}`,
                label: action.label,
                variant: (action.mode === "create" ? "primary" : "secondary") as "primary" | "secondary",
                disabled: busy || invoiceBusy,
                onClick: () => handleInvoiceAction(action),
              })),
            ]}
          />
        ) : (
          <div className="flex justify-end">
            <button type="button" className="text-sm text-label-secondary" onClick={onClose}>
              Schließen
            </button>
          </div>
        )
      }
    >
      {loading && !ts ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : null}

      {error ? <p className="mb-3 text-sm text-status-red">{error}</p> : null}

      {ts ? (
        <div className="space-y-3">
          {metaParts.length > 0 ? (
            <p className="text-xs text-label-secondary">{metaParts.join(" · ")}</p>
          ) : null}
          <p className="text-xs text-label-secondary">
            Primär zuerst: PDF prüfen, dann Versand oder Abrechnung ausführen.
          </p>

          {showExportActions ? (
            <TimesheetExportActions
              timesheetId={timesheetId!}
              timesheet={ts}
              displayNumber={displayNumber}
              busy={busy}
              onPrint={handlePrint}
              onSendMail={handleSendMail}
              onMailSent={async () => {
                await reload();
                onRefresh();
              }}
              onError={setError}
            />
          ) : null}
        </div>
      ) : null}

      {choiceDialog}
    </Dialog>
  );
}
